import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Token storage: sessionStorage is scoped per-tab and cleared on close, reducing
// XSS attack surface compared with localStorage. httpOnly cookies (set by the
// backend) remain the primary auth channel; this token is a Bearer fallback for
// browsers that reject third-party cookies in the preview iframe.
const TOKEN_KEY = "clubfin_access_token";
export const tokenStore = {
  get() { return sessionStorage.getItem(TOKEN_KEY); },
  set(t) { sessionStorage.setItem(TOKEN_KEY, t); },
  clear() { sessionStorage.removeItem(TOKEN_KEY); },
};

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiError(detail) {
  // Pydantic v2 prefixes custom-validator errors with "Value error, " — strip
  // that so the user sees the actual Dutch message we authored.
  const cleanMsg = (s) => (typeof s === "string" ? s.replace(/^Value error,\s*/i, "") : s);
  if (detail == null) return "Er is iets misgegaan.";
  if (typeof detail === "string") return cleanMsg(detail);
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? cleanMsg(e.msg) : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return cleanMsg(detail.msg);
  return String(detail);
}

export const fmtCurrency = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}€${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}€${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}€${(abs / 1_000).toFixed(1)}K`;
  return `${sign}€${abs.toFixed(0)}`;
};

// Whole-euro integer formatter with NL thousand separators, no currency symbol, no K/M
// suffix. Used throughout tables and reports so numbers read consistently.
export const fmtInt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
};

// Parse a NL-formatted numeric string back to a JS number.
// Accepts "196.000", "-196.000,50", "196000" and returns NaN for empty/invalid input.
export const parseIntNL = (raw) => {
  if (raw === null || raw === undefined) return NaN;
  const s = String(raw).trim();
  if (s === "" || s === "-") return NaN;
  // Strip everything except digits, minus sign, dot and comma; then treat dots as
  // thousand separators (removed) and commas as decimal (converted to dot).
  const cleaned = s.replace(/[^0-9.,-]/g, "").replace(/\./g, "").replace(/,/g, ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

// Full-precision Euro formatter for reconciliation/audit tables.
export const fmtEur = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

export const fmtPct = (n) => (n == null || Number.isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`);
export const fmtYears = (n) => (n == null || Number.isNaN(n) ? "—" : `${n.toFixed(1)} yr`);
